import React, { useEffect, useState } from "react";

const defaultFaq = [
  {
    faq_title: "Boosting Revenue",
    faq_list: [
      {
        question: "How will Laguna Creek help me grow my business?",
        answer:
          "<p>The hospitality and tourism market is highly competitive, and growing your business in that market requires tools that save on costs, provide actionable analytics, and not only reach new customers but make it as seamless as possible for them to choose to book with you.</p>\n<p>With Laguna Creek, you will be able to optimize all aspects of your business that bring in customers. This includes channel management, affiliate management, dynamic pricing and integrations with point of sale systems, and even being able to bundle with nearby properties and tours.</p>\n<p>With Laguna Creek, you will eliminate lost opportunities for sales, while greatly reducing labor hours and expenses.</p>\n",
      },
      {
        question:
          "Can I use Laguna Creek for my RV resort?  What about our amenities?",
        answer:
          "<p>Absolutely. There is no reason not to offer your guests the ability to book their RV spaces online, which is not only convenient but will greatly increase sales. Having Laguna Creek would also make it much easier to track your existing RV reservations, manage available spots for affiliates and keep better records for your business.</p>\n<p>Your guests will also be able to book a restaurant reservation, spa appointment, or tee time when they select their room. Studies have shown that bookings for restaurants and amenities increase significantly when guests can easily combine them with their hotel reservation.</p>\n",
      },
      {
        question: "How does Laguna Creek help Tribal properties and tours?",
        answer:
          "<p>Laguna Creek is a 100% Native-owned and managed company, so we fully understand cultural awareness and the importance of tribal digital sovereignty.</p>\n<p>Our reporting includes Tribal permit fees and taxes, and makes NIGC compliance easier. Our platform allows guests to not only book a room in a Tribal property, but also add on local Tribal tour times for nearby attractions on Indian land. Our Point of Sale system is ideal for local Native artisans and food trucks offering goods near Tribal properties to boost sales and keep customers happy.</p>\n",
      },
    ],
  },
  {
    faq_title: "Cutting Expenses",
    faq_list: [
      {
        question: "How much does Laguna Creek cost? Is there a subscription?",
        answer:
          "<p>There are no subscriptions with Laguna Creek, and no direct costs to our clients. Our pricing model simply adds a small fee when the customers book through our system, making Laguna Creek the most cost effective software on the market.</p>\n",
      },
      {
        question:
          "Are there any hidden fees or surprise charges? Do I have to purchase additional software products from you?",
        answer: "<p>No, no and no.</p>\n",
      },
      {
        question: "Do you charge OTA fees?",
        answer:
          "<p>When using the Laguna Creek booking platform, you will not have to pay 25-30% commissions to an Online Travel Agency (OTA) for reservations made on your website. And unlike other booking engines, we do not charge an OTA fee.</p>\n",
      },
      {
        question: "How does Laguna Creek improve operational efficiency?",
        answer:
          "<p>Laguna Creek eliminates the need for multiple subscriptions to different &#8211; sometimes incompatible &#8211; software systems. Everything your staff needs to seamlessly manage every aspect of your operations &#8211; from the kitchen to housekeeping to the front desk &#8211; is on one platform.</p>\n<p>The Laguna Creek automates complex tasks like booking, billing and reporting. Ease of use and simplicity greatly reduces the risk of costly errors. Best in industry customer support ensures that any questions are answered quickly, by a real human.</p>\n<p>We understand that your team&#8217;s time is your most valuable asset. Laguna Creek was designed to reduce errors, duplicity and confusion, while automating cumbersome tasks, so your staff can devote more time and energy where it belongs &#8211; with the customer.</p>\n",
      },
    ],
  },
  {
    faq_title: "Peace of Mind",
    faq_list: [
      {
        question:
          "How do you guarantee the security of your software and protect my customers’ information?",
        answer:
          "<p>The system uses military grade encryption to protect guest information from cyber threats like hackers and ransomware. Your data, and your customers’ data, are safe with Laguna Creek.</p>\n",
      },
      {
        question: "Can your system work with our desktop and mobile devices?",
        answer:
          "<p>Yes, our software and applications can be installed on any operating system.</p>\n",
      },
      {
        question: "How accurate and timely are your reporting features?",
        answer:
          "<p>Reports on every facet of your operations are available whenever you need them and in real time. We understand how important it is to have accurate reporting at all times to maintain compliance and manage your business. Reporting includes revenues, expenses, taxes &#8211; including Tribal taxes &#8211; permit fees and much, much more.</p>\n",
      },
      {
        question: "How long will it take for my team to learn your system? ",
        answer:
          "<p>Not long at all. Simplicity and interoperability were the primary goals in the design and development of Laguna Creek. With a dedicated representative available to train your staff and answer questions, even staff with limited or no experience with other Property Management Systems will be able to seamlessly operate the Laguna Creek system in no time.</p>\n",
      },
    ],
  },
];

const MainFaqsSection = ({ acfData }) => {
  // Set default FAQ data or load from props
  const [faqList, setFaqList] = useState([]);
  const [activeIndexes, setActiveIndexes] = useState({});

  // Initialize state from default or acfData
  useEffect(() => {
    const data = acfData && acfData.length > 0 ? acfData : defaultFaq;
    const initialIndexes = {};
    data.forEach((section, idx) => {
      initialIndexes[`faq_${idx}`] = [0]; // First item open by default
    });
    setFaqList(data);
    setActiveIndexes(initialIndexes);
  }, [acfData]);

  // Toggle open/close FAQ item
  const toggleFAQ = (sectionKey, index) => {
    setActiveIndexes((prev) => {
      const current = prev[sectionKey] || [];
      const isActive = current.includes(index);

      return {
        ...prev,
        [sectionKey]: isActive ? [] : [index], // Only one open at a time
      };
    });
  };

  // Render one section of FAQs
  const renderFAQSection = (title, faqs, sectionKey, isLast = false) => (
    <div
      className={`flex flex-wrap -mx-4 gap-y-8 ${
        isLast ? "mb-0" : "mb-10 lg:mb-20"
      }`}
      key={sectionKey}
    >
      <div className="w-full lg:w-2/6 max-w-full px-4">
        <div className="lg:max-w-[353px] text-center lg:text-left">
          <h2 className="text-[28px] md:text-[36px] font-normal leading-[1.3056] text-[#192020]">
            {title}
          </h2>
        </div>
      </div>
      <div className="w-full lg:w-4/6 max-w-full px-4">
        <div className="max-w-[997px] ml-auto">
          {faqs.map((faq, index) => {
            const isActive =
              activeIndexes[sectionKey] &&
              activeIndexes[sectionKey].includes(index);
            return (
              <div
                key={index}
                className={`${
                  isActive
                    ? "border-t-0 border-[rgba(0,0,0,0.11)]"
                    : index !== 0
                    ? "border-t border-[rgba(0,0,0,0.11)]"
                    : "border-t-0 border-[rgba(0,0,0,0.11)]"
                }`}
              >
                <button
                  onClick={() => toggleFAQ(sectionKey, index)}
                  className={`${
                    isActive
                      ? "w-full bg-[#EAF9FD] py-[17px] px-[18px] flex justify-between items-center gap-x-8 cursor-pointer text-left rounded-[21px] shadow-[0_2px_0px_0px_rgba(0,0,0,0.12)]"
                      : "w-full bg-white py-[17px] px-[18px] flex justify-between items-center gap-x-8 cursor-pointer text-left"
                  }`}
                >
                  <h3
                    className={`text-lg font-bold transition-colors ${
                      isActive ? "text-[#5FA3B2]" : "text-[#192020]"
                    }`}
                  >
                    {faq.question}
                  </h3>
                  <svg
                    className={`transform transition-transform duration-300 min-w-4 ${
                      isActive ? "rotate-180" : "rotate-0"
                    }`}
                    width="16"
                    height="10"
                    viewBox="0 0 16 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.34905 9.4079C8.06941 9.4079 7.78964 9.30117 7.57653 9.08772L1.01939 2.53058C0.592494 2.10368 0.592494 1.41211 1.01939 0.985212C1.44629 0.558315 2.13786 0.558315 2.56476 0.985212L8.34905 6.77138L14.1344 0.986066C14.5613 0.559168 15.2528 0.559168 15.6797 0.986066C16.1066 1.41296 16.1066 2.10454 15.6797 2.53143L9.12259 9.08858C8.90914 9.30202 8.62909 9.4079 8.34905 9.4079Z"
                      fill={isActive ? "#5FA3B2" : "#2A3C51"}
                    />
                  </svg>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out px-[18px] ${
                    isActive ? "max-h-[500px] py-[17px]" : "max-h-0 py-0"
                  }`}
                >
                  <div
                    className="text-lg font-normal leading-7 text-[#333333]"
                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <section className="py-10 lg:py-20">
      <div className="w-full max-w-[1570px] px-4 mx-auto">
        {faqList.map((value, index) =>
          renderFAQSection(
            value.faq_title,
            value.faq_list,
            `faq_${index}`,
            faqList.length - 1 === index
          )
        )}
      </div>
    </section>
  );
};

export default MainFaqsSection;
